<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <div
      v-if="!isLiteView()"
      class="progress-container"
    >
      <div class="p-medium">
        <p
          class="tertiary-link top-margin-zero small"
        >
          <a :href="portfolioSummaryUrl">&lt; Go to your portfolio summary</a>
        </p>
      </div>
    </div>

    <div class="pvd-container">
      <h1 class="pvd-heading-XL next-steps-heading">
        <div>Next steps</div>
        <div
          v-if="rc_accountNumber"
          class="next-steps-heading--acct-num"
        >
          Trust account {{ rc_accountNumber }}
        </div>
      </h1>
      <p>
        Now that your new trust account is open, you can begin to move money and set investment goals.
      </p>
      <div
        v-if="displayTOACard"
        class="pvd-card"
      >
        <h3 class="pvd-heading-S">
          Transfer assets into your account
        </h3>
        <p>
          We make it easy to transfer all or part of an account to Fidelity—including stocks, bonds, mutual funds, and other security types.
        </p>
        <form
          :action="queryParams.returnUrl"
          method="POST"
        >
          <input
            type="hidden"
            name="toaHandshakeID"
            :value="queryParams.transactionId"
          >
          <input
            type="hidden"
            name="acctnum"
            :value="rc_accountNumber"
          >
          <p>
            <a
              class="pvd-btn btn-action"
              rel="noopener"
              href="javascript: void(0)"
              @click="returnToTOA($event)"
            >
              Continue a transfer
            </a>
          </p>
        </form>
      </div>
      <div
        v-if="displayMMCard"
        class="pvd-card"
      >
        <h3 class="pvd-heading-S">
          Move money into your account
        </h3>
        <p>
          To get your account ready for trading and investing with no fees or minimums<sup>*</sup>,
          follow our guide to easily add money by electronic funds transfer (EFT), mobile check deposit,
          or by transferring your assets.
        </p>
        <p>
          <a
            class="pvd-btn btn-action"
            target="_blank"
            rel="noopener"
            :href="transferURL"
          >
            Move money
          </a>
        </p>
      </div>
      <div
        v-if="displayPlanningCard"
        class="pvd-card"
      >
        <h3 class="pvd-heading-S">
          Set your investment goals and strategy
        </h3>
        <p>
          Whether you want to plan for retirement, pay for college, or create an investment strategy,
          Fidelity's Planning and Guidance Center can help you plan for your goals and track your progress.
        </p>
        <p>
          <a
            class="pvd-btn btn-action"
            target="_blank"
            rel="noopener"
            :href="planningURL"
          >
            Start planning
          </a>
        </p>
      </div>

      <p
        v-if="!isLiteView()"
        class="p-medium text-right"
      >
        <a
          :href="portfolioSummaryUrl"
          class="text-bold"
        >
          Go to your portfolio summary &gt;
        </a>
        <br>
        View and manage your accounts
      </p>

      <div class="m-xlarge-top">
        <p class="xsmall color-neutral pvd-paragraph">
          <sup><strong>
            * Zero account minimums and zero account fees apply to retail brokerage accounts only. Expenses charged by investments
            (e.g., funds, managed accounts, and certain HSAs) and commissions, interest charges, or other expenses for transactions
            may still apply. See
            <a
              target="_blank"
              rel="noopener"
              :href="commissionURL"
            >
              Fidelity.com/commissions
            </a>
            for further details.
          </strong></sup>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import dataHelper from '../mixins/dataHelper';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import measurement from '../mixins/measurement';
import utility from '../mixins/utility';

export default {
  name: 'TrustNextSteps',
  components: {
  },
  mixins: [dataHelper, measurement, utility, pageNavigationHelper],
  data() {
    return {
      additionalUsers: [],
      hasMultipleSigners: false,
      backButtonAttempt: false,
      backButtonDisabledWarning: 'To navigate, please use the buttons or links provided on the page.',
    };
  },
  computed: {
    displayTOACard() {
      return this.queryParams.source === 'TOA';
    },
    displayMMCard() {
      return true;
    },
    displayPlanningCard() {
      return this.queryParams.source !== 'TOA';
    },
    portfolioSummaryUrl() {
      const targetUrl = this.pd_envConfig.portfolioSummaryUrl;
      return (!this.uc_isCustomer && this.uc_hasCustomerCredentials)
        ? `${this.pd_envConfig.retailLoginUrl}${encodeURIComponent(targetUrl)}`
        : targetUrl;
    },
    transferURL() {
      const targetUrl = this.pd_envConfig.transferURL;
      return (!this.uc_isCustomer && this.uc_hasCustomerCredentials)
        ? `${this.pd_envConfig.retailLoginUrl}${encodeURIComponent(targetUrl)}`
        : targetUrl;
    },
    planningURL() {
      const targetUrl = this.pd_envConfig.planningURL;
      return (!this.uc_isCustomer && this.uc_hasCustomerCredentials)
        ? `${this.pd_envConfig.retailLoginUrl}${encodeURIComponent(targetUrl)}`
        : targetUrl;
    },
    commissionURL() {
      const targetUrl = this.pd_envConfig.commissionURL;
      return (!this.uc_isCustomer && this.uc_hasCustomerCredentials)
        ? `${this.pd_envConfig.retailLoginUrl}${encodeURIComponent(targetUrl)}`
        : targetUrl;
    },
  },
  created() {
    try {
      if (!this.uc_isCustomer) {
        const stored = sessionStorage.getItem('hasCustomerCredentials');
        this.uc_hasCustomerCredentials = stored !== null ? JSON.parse(stored) : false;
      }
    } catch (error) {
      console.error('Failed to read hasCustomerCredentials from sessionStorage on TrustNextSteps:', error);
    }
  },
  mounted() {
    if (localStorage.getItem('contextKey')) {
      localStorage.removeItem('contextKey');
    }
    if (localStorage.getItem('transactionContextKey')) {
      localStorage.removeItem('transactionContextKey');
    }
    this.track(this.cs_stateName, 'pageview');
    document.title = this.setTitle('Next steps');
    document.body.style.backgroundColor = '#f9f9f9';

    this.additionalUsers = this.getInstanceData().formData.additionalUsers;
    this.hasMultipleSigners = this.additionalUsers.filter((person) => person.role === 'trustee' || person.role === 'trustee/grantor').length > 0;

    this.configureBackButtonFunctionality(() => this.showBackWarning(
      this.backButtonDisabledWarning,
    ));
  },
  methods: {
    nextButtonClicked() {
      this.performNavigation(this.pd_content.primary_button.value);
    },
    returnToTOA(event) {
      event.target.closest('form').submit();
    },
  },
};
</script>

<style scoped>
  .next-steps-heading {
    display: flex;
  }

  .next-steps-heading--acct-num {
    margin-left: auto;
    font-weight: 700;
    font-size: 1.125rem;
  }
</style>
